import React from 'react';
import {
  Dialog, DialogTitle, DialogContent,
  Typography, Box, LinearProgress, Chip, Paper, Stack, IconButton
} from '@mui/material';
import {
  AutoAwesome as AIIcon, CheckCircle, Warning, Error as ErrorIcon,
  TipsAndUpdates as TipIcon, Close as CloseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const severityColors = { high: '#ef4444', medium: '#f59e0b', low: '#22c55e' };
const severityIcons = { high: <ErrorIcon fontSize="small" />, medium: <Warning fontSize="small" />, low: <CheckCircle fontSize="small" /> };

export default function AICritiqueModal({ open, onClose, critiqueData, loading }) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, background: 'var(--dialog-bg)' } }}>
        <DialogContent sx={{ py: 6, textAlign: 'center' }}>
          <AIIcon sx={{ fontSize: 48, color: 'var(--ai-accent)', mb: 2, animation: 'spin 2s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0)' }, '100%': { transform: 'rotate(360deg)' } } }} />
          <Typography variant="h6">{t('ai.analyzingRoadmap')}</Typography>
          <LinearProgress sx={{ mt: 2, mx: 'auto', maxWidth: 300, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #7c3aed, #6366f1)' } }} />
        </DialogContent>
      </Dialog>
    );
  }

  if (!critiqueData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, background: 'var(--dialog-bg)' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon sx={{ color: 'var(--ai-accent)' }} />
          <Typography variant="h6" fontWeight={700}>{t('ai.critiqueTitle')}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Score */}
        <Paper sx={{ p: 2, mb: 2, background: 'rgba(99,102,241,0.1)', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h3" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #a855f7, #6366f1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {critiqueData.overall_score}/100
          </Typography>
          <Typography variant="body2" color="text.secondary">{critiqueData.summary}</Typography>
          <Chip label={critiqueData.readiness_verdict} sx={{ mt: 1 }}
            color={critiqueData.overall_score >= 75 ? 'success' : critiqueData.overall_score >= 50 ? 'warning' : 'error'} />
        </Paper>

        {/* Strengths */}
        {critiqueData.strengths?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircle sx={{ color: 'var(--status-green)' }} fontSize="small" /> {t('ai.strengths')}
            </Typography>
            <Stack spacing={0.5}>
              {critiqueData.strengths.map((s, i) => (
                <Typography key={i} variant="body2" sx={{ pl: 3 }}>• {s}</Typography>
              ))}
            </Stack>
          </Box>
        )}

        {/* Critical Gaps */}
        {critiqueData.critical_gaps?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Warning sx={{ color: 'var(--status-amber)' }} fontSize="small" /> {t('ai.criticalGaps')}
            </Typography>
            <Stack spacing={1}>
              {critiqueData.critical_gaps.map((g, i) => (
                <Paper key={i} sx={{ p: 1.5, background: 'rgba(245,158,11,0.08)', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {severityIcons[g.severity]}
                    <Typography variant="body2" fontWeight={600}>{g.area}</Typography>
                    <Chip label={g.severity} size="small" sx={{ bgcolor: severityColors[g.severity] + '22', color: severityColors[g.severity], fontWeight: 600 }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">{g.issue}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>💡 {g.suggestion}</Typography>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {/* Quick Wins */}
        {critiqueData.quick_wins?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TipIcon sx={{ color: 'var(--ai-accent)' }} fontSize="small" /> {t('ai.quickWins')}
            </Typography>
            <Stack spacing={0.5}>
              {critiqueData.quick_wins.map((w, i) => (
                <Typography key={i} variant="body2" sx={{ pl: 3 }}>⚡ {w}</Typography>
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
